import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Tabs, Tab, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { loadBase, loadPlayerStats, loadDefensiveStats } from '../../services/espnService';
import { useConfig } from '../../hooks/useConfig';

// ── Constants ─────────────────────────────────────────────────────────────────
const OFF_POSITIONS = ['QB', 'RB', 'WR', 'TE'];
const DEF_POSITIONS = ['DE', 'DT', 'LB', 'CB', 'S'];
const MIN_GAMES_OFF = { QB: 8, RB: 6, WR: 6, TE: 6 };

const DEF_FORMULAS = {
  DE: ['sacks', 'QBHits', 'tacklesForLoss', 'totalTackles', 'forcedFumbles'],
  DT: ['sacks', 'QBHits', 'tacklesForLoss', 'totalTackles', 'forcedFumbles'],
  LB: ['totalTackles', 'sacks', 'tacklesForLoss', 'passesDefended', 'forcedFumbles'],
  CB: ['passesDefended', 'interceptions', 'totalTackles'],
  S:  ['totalTackles', 'passesDefended', 'interceptions'],
};
const DEF_POS_SLOTS = { DE: 2, DT: 2, LB: 3, CB: 2, S: 2 };
const DEF_MIN_GAMES = 6;

// ESPN team abbreviation normalization (ESPN sometimes differs from our keys)
const ESPN_TEAM_MAP = {
  WSH: 'WAS', WAS: 'WAS', JAC: 'JAX', JAX: 'JAX',
  LAR: 'LAR', LA: 'LAR', LV: 'LV', LAS: 'LV',
};
function normTeam(t) { return ESPN_TEAM_MAP[t] || t; }

// ── Schedule fetcher ──────────────────────────────────────────────────────────
// Returns { weekNumber, season, scheduleMap: { [teamAbbr]: opponentAbbr | 'BYE' } }
// or null if no regular season schedule is available
let _scheduleCache = {}; // keyed by "season-week"

async function fetchWeekSchedule(week, season) {
  const cacheKey = `${season}-${week}`;
  if (_scheduleCache[cacheKey]) return _scheduleCache[cacheKey];

  try {
    const url = `https://site.api.espn.com/apis/site/v2/sports/football/nfl/scoreboard?seasontype=2&week=${week}&dates=${season}`;
    const res = await fetch(url);
    const data = await res.json();
    const events = data.events || [];

    const scheduleMap = {};
    events.forEach(event => {
      const comps = event.competitions?.[0]?.competitors || [];
      if (comps.length === 2) {
        const [a, b] = comps;
        const teamA = normTeam(a.team?.abbreviation || '');
        const teamB = normTeam(b.team?.abbreviation || '');
        if (teamA) scheduleMap[teamA] = teamB;
        if (teamB) scheduleMap[teamB] = teamA;
      }
    });

    const result = {
      weekNumber: data.week?.number ?? week,
      season: data.season?.year ?? season,
      games: events.length,
      scheduleMap,
    };
    _scheduleCache[cacheKey] = result;
    return result;
  } catch {
    return null;
  }
}

// Fetch current week info from ESPN scoreboard (no week param = current)
async function fetchCurrentWeekInfo() {
  try {
    const res = await fetch('https://site.api.espn.com/apis/site/v2/sports/football/nfl/scoreboard');
    const data = await res.json();
    return {
      week: data.week?.number ?? 1,
      season: data.season?.year ?? 2026,
      seasonType: data.season?.type ?? 2,
    };
  } catch {
    return { week: 1, season: 2026, seasonType: 2 };
  }
}

// ── FPPG (PPR) ────────────────────────────────────────────────────────────────
// formula comes from config (nr_formula_qb/rb/wr/te) — same source as front page cards
function calcFppg(pos, s, formula = {}) {
  if (!s || !s.gamesPlayed) return 0;
  const gp = s.gamesPlayed;
  let raw = 0;
  if (pos === 'QB') {
    raw = (s.passingYards || 0)        * (formula.passYards  ?? 0.04)
        + (s.passingTouchdowns || 0)   * (formula.passTd     ?? 4)
        + (s.interceptions || 0)       * (formula.int        ?? -2)
        + (s.rushingYards || 0)        * (formula.rushYards  ?? 0.1)
        + (s.rushingTouchdowns || 0)   * (formula.rushTd     ?? 6);
  } else if (pos === 'RB') {
    raw = (s.rushingYards || 0)        * (formula.rushYards  ?? 0.1)
        + (s.rushingTouchdowns || 0)   * (formula.rushTd     ?? 6)
        + (s.receptions || 0)          * (formula.receptions ?? 1)
        + (s.receivingYards || 0)      * (formula.recYards   ?? 0.1)
        + (s.receivingTouchdowns || 0) * (formula.recTd      ?? 6);
  } else {
    raw = (s.receptions || 0)          * (formula.receptions ?? 1)
        + (s.receivingYards || 0)      * (formula.recYards   ?? 0.1)
        + (s.receivingTouchdowns || 0) * (formula.recTd      ?? 6);
  }
  return raw / gp;
}

// ── Defensive team rank computation ──────────────────────────────────────────
function computeDefPosNR(pos, players, defStats, weights) {
  const statKeys = DEF_FORMULAS[pos];
  const eligible = players.filter(p => {
    const s = defStats[p.id];
    return s && s.gamesPlayed >= DEF_MIN_GAMES;
  }).map(p => {
    const s = defStats[p.id];
    const gp = s.gamesPlayed;
    const perGame = {};
    statKeys.forEach(k => { perGame[k] = gp > 0 ? s[k] / gp : 0; });
    return { ...p, perGame };
  });

  const maxRank = eligible.length + 1;
  if (!eligible.length) {
    const nrMap = {};
    players.forEach(p => { nrMap[p.id] = maxRank; });
    return { nrMap, maxRank };
  }

  const statRanks = {};
  eligible.forEach(p => { statRanks[p.id] = {}; });
  statKeys.forEach(k => {
    const sorted = [...eligible].sort((a, b) => b.perGame[k] - a.perGame[k]);
    let rank = 1, i = 0;
    while (i < sorted.length) {
      const tieVal = sorted[i].perGame[k]; let j = i;
      while (j < sorted.length && sorted[j].perGame[k] === tieVal) { statRanks[sorted[j].id][k] = rank; j++; }
      rank += j - i; i = j;
    }
  });

  const withNR = eligible.map(p => ({
    ...p,
    rankSum: statKeys.reduce((acc, k) => acc + (statRanks[p.id][k] ?? eligible.length) * (weights[k] ?? 1), 0),
  })).sort((a, b) => a.rankSum - b.rankSum);

  const nrMap = {};
  let nr = 1, i = 0;
  while (i < withNR.length) {
    const tieSum = withNR[i].rankSum; let j = i;
    while (j < withNR.length && withNR[j].rankSum === tieSum) { nrMap[withNR[j].id] = nr; j++; }
    nr += j - i; i = j;
  }
  players.forEach(p => { if (!(p.id in nrMap)) nrMap[p.id] = maxRank; });
  return { nrMap, maxRank };
}

function computeDefTeamRankMap(defPlayers, defStats, config) {
  const posData = {};
  DEF_POSITIONS.forEach(pos => {
    const posPlayers = Object.values(defPlayers).filter(p => p.posKey === pos);
    const weights = config[`nr_weights_def_${pos.toLowerCase()}`] ?? {};
    posData[pos] = computeDefPosNR(pos, posPlayers, defStats, weights);
  });

  const teamMap = {};
  Object.values(defPlayers).forEach(p => {
    if (!teamMap[p.team]) teamMap[p.team] = { DE: [], DT: [], LB: [], CB: [], S: [] };
    if (DEF_POSITIONS.includes(p.posKey)) teamMap[p.team][p.posKey].push(p);
  });

  const rows = Object.entries(teamMap).map(([team, byPos]) => {
    let totalScore = 0;
    DEF_POSITIONS.forEach(pos => {
      const { nrMap, maxRank } = posData[pos];
      const slots = DEF_POS_SLOTS[pos] ?? 1;
      const playerNRs = (byPos[pos] || []).map(p => nrMap[p.id] ?? maxRank).sort((a, b) => a - b);
      const filled = [...playerNRs.slice(0, slots)];
      while (filled.length < slots) filled.push(maxRank);
      totalScore += filled.reduce((s, v) => s + v, 0) / slots;
    });
    totalScore /= DEF_POSITIONS.length;
    return { team, totalScore };
  });

  rows.sort((a, b) => a.totalScore - b.totalScore);
  const defRankMap = {};
  rows.forEach((r, i) => { defRankMap[r.team] = i + 1; });
  return defRankMap;
}

// ── Matchup helpers ───────────────────────────────────────────────────────────
function matchupTier(defRank) {
  if (defRank <= 10)  return 'tough';
  if (defRank >= 23)  return 'soft';
  return 'neutral';
}
const TIER_COLOR = { tough: '#ef4444', neutral: '#f5a623', soft: '#2ea84b' };
const TIER_LABEL = { tough: 'Tough', neutral: 'Neutral', soft: 'Soft' };

// ── Styles ────────────────────────────────────────────────────────────────────
const cellSx = {
  color: '#9eb8d0', fontSize: '0.75rem', borderColor: 'rgba(30,52,72,0.5)', py: 0.5, px: 0.75,
};
const headerCellSx = {
  color: '#2ea84b', fontWeight: 700, fontSize: '0.7rem', borderColor: 'rgba(30,52,72,0.5)',
  py: 0.5, px: 0.75, backgroundColor: '#060e1a',
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function FantasyProjections() {
  const { config } = useConfig();
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [offPlayers, setOffPlayers] = useState({});
  const [offStats, setOffStats]     = useState({});
  const [defPlayers, setDefPlayers] = useState({});
  const [defStats, setDefStats]     = useState({});
  const [posTab, setPosTab]         = useState(0);

  // Schedule state
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [scheduleData, setScheduleData] = useState(null); // { scheduleMap, games, weekNumber }
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [currentWeekInfo, setCurrentWeekInfo] = useState(null); // { week, season, seasonType }

  // Load player data
  useEffect(() => {
    setLoading(true);
    loadBase()
      .then(({ players: p }) => {
        const off = {}, def = {};
        Object.values(p).forEach(pl => {
          if (OFF_POSITIONS.includes(pl.posKey)) off[pl.id] = pl;
          else if (DEF_POSITIONS.includes(pl.posKey)) def[pl.id] = pl;
        });
        return Promise.all([
          loadPlayerStats(Object.keys(off)).then(stats => [off, stats]),
          loadDefensiveStats(Object.keys(def)).then(stats => [def, stats]),
        ]);
      })
      .then(([[off, os], [def, ds]]) => {
        setOffPlayers(off);
        setOffStats(os);
        setDefPlayers(def);
        setDefStats(ds);
        setLoading(false);
      })
      .catch(() => { setError('Failed to load fantasy projection data.'); setLoading(false); });
  }, []);

  // Detect current week and season on mount
  useEffect(() => {
    fetchCurrentWeekInfo().then(info => {
      setCurrentWeekInfo(info);
      // Only auto-set week for regular season (type 2); otherwise default to week 1 of 2026
      const week = info.seasonType === 2 ? info.week : 1;
      setSelectedWeek(week);
    });
  }, []);

  // Fetch schedule whenever week changes
  useEffect(() => {
    if (!currentWeekInfo) return;
    const season = currentWeekInfo.seasonType === 2 ? currentWeekInfo.season : 2026;
    setScheduleLoading(true);
    fetchWeekSchedule(selectedWeek, season).then(result => {
      setScheduleData(result);
      setScheduleLoading(false);
    });
  }, [selectedWeek, currentWeekInfo]);

  // Defensive team rank map
  const defRankMap = useMemo(() => {
    if (!Object.keys(defPlayers).length || !Object.keys(defStats).length) return {};
    return computeDefTeamRankMap(defPlayers, defStats, config);
  }, [defPlayers, defStats, config]);

  const currentPos = OFF_POSITIONS[posTab];
  const matchupWeight = config.projection_settings?.matchupWeight ?? 0.01;
  const hasSchedule = (scheduleData?.games ?? 0) > 0;

  // Use the same scoring formula as the front-page fantasy cards (from config)
  const formulaByPos = {
    QB: config.nr_formula_qb,
    RB: config.nr_formula_rb,
    WR: config.nr_formula_wr,
    TE: config.nr_formula_te,
  };

  const rows = useMemo(() => {
    if (!Object.keys(offPlayers).length || !Object.keys(offStats).length) return [];
    const minGames = MIN_GAMES_OFF[currentPos] ?? 6;
    const sm = scheduleData?.scheduleMap || {};
    const formula = formulaByPos[currentPos] || {};

    return Object.values(offPlayers)
      .filter(p => {
        if (p.posKey !== currentPos) return false;
        const s = offStats[p.id];
        return s && s.gamesPlayed >= minGames;
      })
      .map(p => {
        const fppg = calcFppg(currentPos, offStats[p.id], formula);
        const opponent = hasSchedule ? (sm[p.team] || 'BYE') : null;
        const onBye = opponent === 'BYE';
        let projectedPts = fppg;
        let adjPct = null;
        let defRank = null;
        let tier = null;

        if (opponent && !onBye && defRankMap[opponent]) {
          defRank = defRankMap[opponent];
          const adj = (defRank - 16.5) * matchupWeight;
          projectedPts = fppg * (1 + adj);
          adjPct = (adj * 100).toFixed(1);
          tier = matchupTier(defRank);
        }

        return { id: p.id, name: p.name, shortName: p.shortName || p.name, team: p.team, fppg, projectedPts, adjPct, defRank, tier, opponent, onBye };
      })
      .sort((a, b) => b.projectedPts - a.projectedPts);
  }, [offPlayers, offStats, currentPos, scheduleData, defRankMap, matchupWeight, hasSchedule, config]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
      <CircularProgress size={36} sx={{ color: '#2ea84b' }} />
      <Typography variant="body2" sx={{ color: '#9eb8d0' }}>Loading fantasy projections…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ color: '#ef4444' }}>{error}</Typography>
    </Box>
  );

  const offseasonMode = !hasSchedule && !scheduleLoading;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#e8f0f8' }}>
          Fantasy Projections
        </Typography>
        <Chip label="Nerds+" color="secondary" size="small" sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
        {hasSchedule && (
          <Chip
            label={`Week ${scheduleData.weekNumber}`}
            size="small"
            sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700, bgcolor: 'rgba(46,168,75,0.12)', color: '#2ea84b', border: '1px solid rgba(46,168,75,0.3)' }}
          />
        )}
      </Box>
      <Typography variant="caption" sx={{ color: '#6a8aa8', display: 'block', mb: 2, lineHeight: 1.6 }}>
        PPR FPPG from 2025 season, adjusted per each player's scheduled opponent this week.
        Proj Pts = FPPG × (1 + (DefRank − 16.5) × matchupWeight). Lower def rank = stronger defense.
      </Typography>

      {/* Week selector + position tabs row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {/* Week picker */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#6a8aa8', fontWeight: 600, whiteSpace: 'nowrap' }}>
            Week
          </Typography>
          <ToggleButtonGroup
            value={selectedWeek}
            exclusive
            onChange={(_, v) => { if (v != null) setSelectedWeek(v); }}
            size="small"
            sx={{ flexWrap: 'wrap', gap: 0.25 }}
          >
            {Array.from({ length: 18 }, (_, i) => i + 1).map(w => (
              <ToggleButton
                key={w}
                value={w}
                sx={{
                  minWidth: 30, height: 24, fontSize: '0.65rem', fontWeight: 700, px: 0.5,
                  color: selectedWeek === w ? '#2ea84b' : '#6a8aa8',
                  borderColor: 'rgba(245,166,35,0.2)',
                  backgroundColor: selectedWeek === w ? 'rgba(46,168,75,0.1)' : 'transparent',
                  '&:hover': { backgroundColor: 'rgba(46,168,75,0.06)' },
                }}
              >
                {w}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {scheduleLoading && <CircularProgress size={14} sx={{ color: '#f5a623', ml: 0.5 }} />}
        </Box>
      </Box>

      {/* Off-season banner */}
      {offseasonMode && (
        <Box sx={{
          mb: 2.5, p: 1.5, borderRadius: 1.5,
          backgroundColor: 'rgba(245,166,35,0.05)',
          border: '1px solid rgba(245,166,35,0.2)',
        }}>
          <Typography sx={{ fontSize: '0.78rem', color: '#f5a623' }}>
            📅 Week {selectedWeek} matchups not yet available — the 2026 schedule hasn't been released.
            Base FPPG rankings are shown without matchup adjustment.
            Opponent columns will auto-populate once the schedule is live.
          </Typography>
        </Box>
      )}

      {/* Position tabs */}
      <Tabs
        value={posTab}
        onChange={(_, v) => setPosTab(v)}
        textColor="inherit"
        indicatorColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          borderBottom: '1px solid rgba(46,168,75,0.15)',
          '& .MuiTab-root': { minHeight: 36, fontWeight: 700, textTransform: 'none', fontSize: '0.82rem', px: 2, color: '#9eb8d0' },
          '& .Mui-selected': { color: '#2ea84b' },
        }}
      >
        {OFF_POSITIONS.map(pos => <Tab key={pos} label={pos} />)}
      </Tabs>

      {/* Table */}
      {rows.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: '#6a8aa8' }}>No qualifying {currentPos}s found.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: '#060e1a', border: '1px solid rgba(30,52,72,0.6)', borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellSx, width: 40 }}>#</TableCell>
                <TableCell sx={{ ...headerCellSx, minWidth: 140 }}>Player</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 64 }}>Team</TableCell>
                <TableCell align="right" sx={{ ...headerCellSx, width: 88 }}>Base FPPG</TableCell>
                <TableCell align="center" sx={{ ...headerCellSx, width: 80 }}>Opponent</TableCell>
                <TableCell align="center" sx={{ ...headerCellSx, width: 80 }}>Def Rank</TableCell>
                <TableCell align="right" sx={{ ...headerCellSx, width: 68 }}>Adj%</TableCell>
                <TableCell align="right" sx={{ ...headerCellSx, width: 96 }}>Proj Pts</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => {
                const tierColor = row.tier ? TIER_COLOR[row.tier] : null;
                return (
                  <TableRow key={row.id} sx={{
                    backgroundColor: idx % 2 === 0 ? 'rgba(6,14,26,0.5)' : 'rgba(10,20,38,0.4)',
                    '&:hover': { backgroundColor: 'rgba(22,44,70,0.6)' },
                    opacity: row.onBye ? 0.5 : 1,
                  }}>
                    <TableCell sx={{ ...cellSx, fontWeight: 900, fontSize: '0.8rem', width: 40, color: idx < 5 ? '#2ea84b' : '#e8f0f8' }}>
                      {idx + 1}
                    </TableCell>

                    <TableCell sx={{ ...cellSx, color: '#e8f0f8', fontWeight: 600 }}>
                      {row.shortName}
                      {row.onBye && (
                        <Chip label="BYE" size="small" sx={{ ml: 0.75, fontSize: '0.55rem', height: 15, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.05)', color: '#4a6080', border: '1px solid rgba(255,255,255,0.08)' }} />
                      )}
                    </TableCell>

                    <TableCell sx={{ ...cellSx, width: 64 }}>
                      <Chip label={row.team} size="small" sx={{
                        fontSize: '0.6rem', height: 18, fontWeight: 700,
                        bgcolor: 'rgba(46,168,75,0.1)', color: '#2ea84b',
                        border: '1px solid rgba(46,168,75,0.25)',
                      }} />
                    </TableCell>

                    <TableCell align="right" sx={{ ...cellSx, color: '#c0d4e8', fontWeight: 700 }}>
                      {row.fppg.toFixed(1)}
                    </TableCell>

                    {/* Per-player opponent */}
                    <TableCell align="center" sx={{ ...cellSx }}>
                      {row.onBye ? (
                        <Typography sx={{ fontSize: '0.68rem', color: '#3a5570' }}>BYE</Typography>
                      ) : row.opponent ? (
                        <Chip
                          label={row.opponent}
                          size="small"
                          sx={{
                            fontSize: '0.6rem', height: 18, fontWeight: 700,
                            bgcolor: tierColor ? tierColor + '22' : 'rgba(255,255,255,0.05)',
                            color: tierColor ?? '#9eb8d0',
                            border: `1px solid ${tierColor ? tierColor + '44' : 'rgba(255,255,255,0.08)'}`,
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: '0.68rem', color: '#3a5570' }}>—</Typography>
                      )}
                    </TableCell>

                    {/* Def Rank */}
                    <TableCell align="center" sx={{ ...cellSx, color: tierColor ?? '#9eb8d0', fontWeight: row.defRank != null ? 700 : 400 }}>
                      {row.defRank != null ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.75rem', color: 'inherit', fontWeight: 700 }}>#{row.defRank}</Typography>
                          {row.tier && (
                            <Chip
                              label={TIER_LABEL[row.tier]}
                              size="small"
                              sx={{ fontSize: '0.52rem', height: 14, fontWeight: 700, bgcolor: tierColor + '22', color: tierColor, border: `1px solid ${tierColor}44` }}
                            />
                          )}
                        </Box>
                      ) : '—'}
                    </TableCell>

                    {/* Adj% */}
                    <TableCell align="right" sx={{
                      ...cellSx,
                      color: row.adjPct != null ? (parseFloat(row.adjPct) >= 0 ? '#2ea84b' : '#ef4444') : '#9eb8d0',
                      fontWeight: row.adjPct != null ? 700 : 400,
                    }}>
                      {row.adjPct != null ? `${parseFloat(row.adjPct) >= 0 ? '+' : ''}${row.adjPct}%` : '—'}
                    </TableCell>

                    {/* Proj Pts */}
                    <TableCell align="right" sx={{
                      ...cellSx,
                      color: hasSchedule && !row.onBye ? '#f5a623' : '#c0d4e8',
                      fontWeight: 800, fontSize: '0.8rem',
                    }}>
                      {row.projectedPts.toFixed(1)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
